/**
 * PO Line Detail Tab - extracted from POLineDetailModal.
 *
 * Renders the main detail view (left column: line fields, right column: changelog).
 */
import React, { useMemo } from 'react';
import { Calendar, Hash, DollarSign } from 'lucide-react';
import type { PurchaseOrderLine } from '../../lib/api';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import OTDOverridePanel from './OTDOverridePanel';

/**
 * CR-4731 round 2: collapse propose+decision row pairs into one row.
 *
 * The backend writes two POChangeLog rows per change-request lifecycle: one
 * for the proposal (orange "Change Request" badge) and one for the
 * accept/reject/cancel decision (green "Change Accepted" badge etc.). The
 * Audit History panel was rendering both, which read to users as the system
 * over-counting the number of changes (Sarah's PRO00222 line 3: 1 request +
 * 1 acceptance = 6 audit rows for 3 fields). Group by (field, ref) and keep
 * the most recent — the decision row when present (correct old/new values
 * after CR-4731 round 1, plus the right badge), or the proposal row while
 * the request is still open. Rows without a change_request_ref (legacy data,
 * GRN events, hold/critical changes) pass through unchanged.
 */
function collapseChangeRequestRows(entries: ChangelogEntry[]): ChangelogEntry[] {
  const latestByKey = new Map<string, ChangelogEntry>();
  const grouped: ChangelogEntry[] = [];
  const passthrough: ChangelogEntry[] = [];
  for (const entry of entries) {
    if (entry.change_request_ref == null) {
      passthrough.push(entry);
      continue;
    }
    const key = `${entry.field_changed}::${entry.change_request_ref}`;
    const existing = latestByKey.get(key);
    if (!existing || new Date(entry.change_date).getTime() > new Date(existing.change_date).getTime()) {
      latestByKey.set(key, entry);
    }
  }
  latestByKey.forEach(entry => grouped.push(entry));
  return [...grouped, ...passthrough].sort(
    (a, b) => new Date(b.change_date).getTime() - new Date(a.change_date).getTime()
  );
}

/* ------------------------------------------------------------------ */
/*  Shared helpers                                                     */
/* ------------------------------------------------------------------ */

/** Status badge configuration matching old portal display mapping. */
export const getAcknowledgeStatusBadge = (status?: string) => {
  if (!status) return null;

  const statusConfig: Record<string, { label: string; className: string }> = {
    new: { label: 'New', className: 'bg-blue-500 text-white' },
    declined: { label: 'Declined', className: 'bg-gray-800 text-white' },
    change_proposed_customer: { label: 'Changed', className: 'bg-amber-500 text-white' },
    change_proposed_supplier: { label: 'Changed', className: 'bg-amber-500 text-white' },
    wip: { label: 'WIP', className: 'bg-indigo-200 text-indigo-900' },
    accepted: { label: 'WIP', className: 'bg-green-500 text-white' },
    despatched: { label: 'Despatched', className: 'bg-gray-200 text-gray-900' },
    change_declined: { label: 'Change Declined', className: 'bg-red-500 text-white' },
    cancelled: { label: 'Cancelled', className: 'bg-gray-600 text-white' },
  };

  const config = statusConfig[status] || { label: status, className: 'bg-gray-200 text-gray-900' };

  return (
    <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
      {config.label}
    </span>
  );
};

// CR-3403: Interface for pending change
export interface PendingChange {
  field: string;
  label: string;
  oldValue: string;
  newValue: string;
  reason?: string;
  changeDate: string;
  changedBy: string;
  icon: React.ReactNode;
}

/** Get pending changes for review based on acknowledge_status. */
export function getPendingChanges(
  line: PurchaseOrderLine,
  changelogs: ChangelogEntry[],
  canShowWorkflow: boolean,
  userPlatform?: string,
  currentUserId?: number,
): PendingChange[] {
  if (!canShowWorkflow) return [];

  const pendingChanges: PendingChange[] = [];
  const status = line.acknowledge_status;

  // CR-4688: If acknowledge_status is no longer change_proposed_*, all
  // proposals have been fully processed — no pending changes to show.
  if (status !== 'change_proposed_customer' && status !== 'change_proposed_supplier') return [];

  const recentChanges = changelogs.filter(log => {
    const changeDate = new Date(log.change_date);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    return changeDate >= thirtyDaysAgo;
  });

  const latestByField: Record<string, ChangelogEntry> = {};
  for (const log of recentChanges) {
    const field = log.field_changed;
    if (!latestByField[field] || new Date(log.change_date) > new Date(latestByField[field].change_date)) {
      latestByField[field] = log;
    }
  }

  const fieldConfig: Record<string, { label: string; icon: React.ReactNode }> = {
    delivery_date: { label: 'Delivery Date', icon: <Calendar className="h-5 w-5" /> },
    supplier_promise_date: { label: 'Supplier Promise Date', icon: <Calendar className="h-5 w-5" /> },
    quantity: { label: 'Quantity', icon: <Hash className="h-5 w-5" /> },
    unit_price: { label: 'Unit Price', icon: <DollarSign className="h-5 w-5" /> },
  };

  for (const [field, config] of Object.entries(fieldConfig)) {
    const log = latestByField[field];
    if (log && log.old_value && log.new_value) {
      const isProposedChange = log.reason?.toLowerCase().includes('proposed') ||
        status === 'change_proposed_customer' ||
        status === 'change_proposed_supplier';

      if (isProposedChange) {
        // Only show changes from the OTHER party
        // 1. Skip if changed_by is the current user (you don't accept your own proposals)
        if (currentUserId && Number(log.changed_by) === currentUserId) continue;
        // 2. Check company_type on the changelog entry
        const changeCompanyType = (log as unknown as Record<string, unknown>)['company_type'] as string | undefined;
        const isSupplier = userPlatform === 'supplier' || userPlatform === 'SUPPLIER' || userPlatform === 'HELIX';
        if (isSupplier && changeCompanyType === 'supplier') continue;
        if (!isSupplier && changeCompanyType === 'customer') continue;

        pendingChanges.push({
          field,
          label: config.label,
          oldValue: log.old_value,
          newValue: log.new_value,
          reason: log.reason,
          changeDate: log.change_date,
          changedBy: log.changed_by,
          icon: config.icon,
        });
      }
    }
  }

  return pendingChanges;
}

/** Get proposer name based on status. */
export function getProposerInfo(line: PurchaseOrderLine) {
  const status = line.acknowledge_status;
  if (status === 'change_proposed_customer') {
    return { name: line.customer_name || 'Customer', type: 'customer' };
  } else if (status === 'change_proposed_supplier') {
    return { name: line.supplier_name || 'Supplier', type: 'supplier' };
  }
  return { name: 'System', type: 'system' };
}

/* ------------------------------------------------------------------ */
/*  Detail Tab Component                                               */
/* ------------------------------------------------------------------ */

interface POLineDetailTabProps {
  line: PurchaseOrderLine;
  changelogs: ChangelogEntry[];
  userType: 'supplier' | 'customer';
  userId?: number;
  formatCurrency: (value: string, currency?: string) => string;
  formatDate: (dateString: string) => string;
  getChangeCount: (fieldName: string) => number | null;
  canShowWorkflowActions: boolean;
  /** CR-4705: Callback to navigate to change requests tab for review. */
  onReviewChanges?: () => void;
}

// CR-4711: Read-only field renderer used throughout the redesigned Detail
// tab. Keeps the visual style consistent and lets us pass an optional
// change-count badge or red-border (supplier-only fields per the spec).
interface ReadOnlyFieldProps {
  label: string;
  value: string | undefined | null;
  badge?: number | null;
  redBorder?: boolean;
  withCalendarIcon?: boolean;
  primaryLabel?: boolean;
}

function ReadOnlyField({ label, value, badge, redBorder, withCalendarIcon, primaryLabel }: ReadOnlyFieldProps) {
  const display = value && value !== '' ? value : '-';
  return (
    <div>
      <label className={`block text-sm font-medium mb-1 ${primaryLabel ? 'text-primary' : 'text-muted-foreground'}`}>{label}</label>
      <div className={`flex items-center gap-2 px-3 py-2 border rounded-lg bg-muted/30 ${redBorder ? 'border-red-300' : 'border-border'}`}>
        {withCalendarIcon && <Calendar className="h-4 w-4 text-muted-foreground" />}
        <span className="flex-1 font-medium truncate">{display}</span>
        {badge ? (
          <span className="px-2 py-0.5 bg-red-100 text-red-800 rounded-full text-xs font-semibold">{badge}</span>
        ) : null}
      </div>
    </div>
  );
}

export function POLineDetailTabContent({
  line,
  changelogs,
  userType,
  formatCurrency,
  formatDate,
  getChangeCount,
}: POLineDetailTabProps) {
  // CR-4711: redesigned per spec (Apr 16 attached design). Most new fields
  // map cleanly to PurchaseOrderLine columns; "Profit Note" had no
  // unambiguous match in the model so it is intentionally omitted until
  // QA clarifies which field to source from.
  // Pricing block + supplier-only red-zone (Contract Unit/Lot/Price, PPV,
  // PDF Attached, Mat Certs) hidden from customers — the customer-side
  // hide rule from CR-4716 (Unit Price suppression) is preserved and
  // extended to all derived/contract values, which the spec marks red.
  const isSupplier = userType === 'supplier';
  // CR-4731 round 2: collapse propose+decision pairs so the Audit History
  // shows one row per change-request lifecycle, not two.
  const collapsedChangelogs = useMemo(() => collapseChangeRequestRows(changelogs), [changelogs]);
  const fmtQty = (q?: string | number | null) => (q === undefined || q === null || q === '') ? '-' : parseFloat(String(q)).toFixed(0);

  return (
    <div className="grid grid-cols-2 gap-4">
      {/* Left Column - Line Details */}
      <div className="card p-4 border rounded-lg">
        <div className="space-y-4">
          {/* Parties row — customer users see their supplier; supplier users see the customer. */}
          {userType === 'customer' && (
            <ReadOnlyField label="Supplier" value={line.supplier_name} />
          )}
          {isSupplier && (
            <div className="grid grid-cols-2 gap-3">
              {line.customer_group_name ? (
                <ReadOnlyField label="Customer Group" value={line.customer_group_name} />
              ) : null}
              <ReadOnlyField label="Customer" value={line.customer_name} />
            </div>
          )}

          {/* Order metadata: Order Date */}
          <div className="grid grid-cols-1 gap-3">
            <ReadOnlyField label="Order Date" value={line.order_date ? formatDate(line.order_date) : ''} withCalendarIcon />
          </div>

          {/* Part identification: Part Number + Description.
              Supplier-portal: Drawing Issue hidden — empty on Cardiff data. */}
          <div className="grid grid-cols-2 gap-3">
            <ReadOnlyField label="Part Number(s)" value={line.part_number} primaryLabel />
            <ReadOnlyField label="Part Description" value={line.description} />
          </div>

          {/* Quantities: Ordered / Delivered / Outstanding */}
          <div className="grid grid-cols-3 gap-3">
            <ReadOnlyField label="Qty Ordered (Total)" value={fmtQty(line.quantity)} badge={getChangeCount('quantity')} primaryLabel />
            <ReadOnlyField label="Qty Delivered" value={fmtQty(line.received_qty)} />
            <ReadOnlyField label="Qty O/S" value={fmtQty(line.outstanding_qty)} />
          </div>

          {/* Dates: the ODD it was agreed for, then the promise as it stands.
              Side by side because the gap between them is the question people
              actually ask — a line promised for December against an agreed
              date in May has slipped, and one date alone never shows that.
              Left to right runs in that order so the pair reads as the slip
              it describes: where it started, where it ended up. */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ReadOnlyField
              label="ODD (Agreed Date)"
              value={line.original_promise_date ? formatDate(line.original_promise_date) : ''}
              badge={getChangeCount('original_promise_date')}
              withCalendarIcon
              primaryLabel
            />
            <ReadOnlyField
              label="Supplier Promise Date"
              value={line.supplier_promise_date ? formatDate(line.supplier_promise_date) : ''}
              badge={getChangeCount('supplier_promise_date')}
              withCalendarIcon
              primaryLabel
            />
          </div>

          {/* CR-4716 (carried forward): Pricing block hidden from customer users.
              The spec's red zone (Contract Unit/Lot/Price, PPV, PDF Attached,
              Mat Certs) is also supplier-only — combining both restrictions
              means customers see no commercial values. */}
          {isSupplier && (
            <>
              {/* Pricing: Unit Price + Currency + Exchange Rate */}
              <div className="grid grid-cols-3 gap-3">
                <ReadOnlyField
                  label="Unit Price"
                  value={formatCurrency(line.unit_price, line.currency)}
                  badge={getChangeCount('unit_price')}
                />
                <ReadOnlyField label="Currency" value={line.currency} />
                <ReadOnlyField label="Exchange Rate" value={line.exchange_rate} />
              </div>

              {/* Totals: Total Value + O/S Value */}
              <div className="grid grid-cols-2 gap-3">
                <ReadOnlyField
                  label="Total Value"
                  value={line.total_price ? formatCurrency(line.total_price, line.currency) : ''}
                />
                <ReadOnlyField
                  label="O/S Value"
                  value={line.outstanding_value ? formatCurrency(line.outstanding_value, line.currency) : ''}
                />
              </div>

              {/* Supplier-portal: "Supplier Proposed Unit Price" (backend
                  field: customer_proposed_unit_price) is intentionally
                  hidden — change-request collaboration is out of scope
                  for this portal instance (CR f13cf024). */}

              {/* Supplier-portal: red-zone commercial fields (Contract
                  Unit/Lot/Price, PPV, PDF Attached, Mat Certs) hidden
                  product-wide — empty on Cardiff data and not relevant
                  to the supplier-portal contract. The block is left
                  intact above (commented-out) so re-enabling is a
                  one-line change if product calls it back. */}

              {/* CR-5263 — manual OTD override (supplier-only). */}
              <div className="mt-4">
                <OTDOverridePanel line={line} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Right Column - Changelog */}
      <div>
        <Changelog entries={collapsedChangelogs} maxHeight="500px" />
      </div>
    </div>
  );
}
